/**
 * Reveal scene — shown briefly after each round resolves (continuous-play variant).
 *
 * Theatre:
 *   1. Eyebrow with category tag (no "ROUND N" — continuous play).
 *   2. Subject NAME explodes in big Bebas Neue with a sparkles burst.
 *   3. Outcome: "+TOTAL pts split across N PLAYERS" or "NOBODY GOT IT" on a
 *      missed round. We keep names off the spotlight (per commit 1fc84bd) —
 *      individual contributions surface on the side-LB's "THIS SESSION" view.
 *
 * Visual language: matches WhatIs answer-reveal (Oswald/Bebas Neue + theme vars).
 */

import type { RoundReveal } from '../state/types.js';

export interface RevealHandle {
  el: HTMLDivElement;
  setReveal(r: RoundReveal): void;
  setVisible(v: boolean): void;
}

const STYLES = `
  @keyframes wai-reveal-burst {
    0%   { opacity: 0; transform: translateY(20px) scale(0.7); letter-spacing: 0.4em; filter: blur(12px); }
    50%  { opacity: 1; transform: translateY(-4px) scale(1.04); letter-spacing: 0.02em; filter: blur(0); }
    100% { opacity: 1; transform: translateY(0)   scale(1.0);  letter-spacing: 0.04em; }
  }
  @keyframes wai-reveal-eyebrow-in {
    from { opacity: 0; transform: translateY(-12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes wai-reveal-points-pop {
    0%   { opacity: 0; transform: scale(0.4); }
    60%  { opacity: 1; transform: scale(1.18); }
    100% { opacity: 1; transform: scale(1); }
  }
  .wai-reveal {
    position: absolute; inset: 0;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center;
    color: var(--sg-text-on-secondary, #FFC43D);
    font-family: 'Oswald', sans-serif;
    padding: 40px 460px 80px 80px;
    background: radial-gradient(ellipse at 50% 35%, rgba(0,240,255,0.12) 0%, var(--sg-secondary, #060CE9) 35%, var(--sg-bg, #0A0E27) 100%);
  }
  .wai-reveal-eyebrow {
    font-family: 'Oswald', sans-serif;
    font-size: 32px; letter-spacing: 0.5em;
    color: var(--sg-accent, #00F0FF);
    text-transform: uppercase;
    opacity: 0.92;
    animation: wai-reveal-eyebrow-in 320ms ease-out both;
    margin-bottom: 22px;
  }
  .wai-reveal-name {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 180px; line-height: 0.95;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 8px 0 var(--sg-secondary, #060CE9), 0 0 60px rgba(255,196,61,0.45);
    animation: wai-reveal-burst 800ms cubic-bezier(0.18, 0.85, 0.4, 1.0) 80ms both;
    word-break: break-word;
    max-width: 1320px;
    text-align: center;
  }
  .wai-reveal-points {
    margin-top: 28px;
    display: inline-flex; align-items: baseline; gap: 14px;
    padding: 14px 28px;
    background: var(--sg-celebration, #22C55E);
    color: #000;
    border-radius: 14px;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 56px;
    letter-spacing: 0.06em;
    box-shadow: 0 0 60px rgba(34,197,94,0.6), 0 8px 28px rgba(0,0,0,0.5);
    animation: wai-reveal-points-pop 520ms cubic-bezier(0.18, 0.85, 0.4, 1.0) 360ms both;
  }
  .wai-reveal-points .small {
    font-family: 'Oswald', sans-serif;
    font-size: 22px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: rgba(0,0,0,0.78);
  }
  .wai-reveal-noWinner {
    margin-top: 28px;
    display: inline-block;
    padding: 14px 28px;
    background: rgba(255,255,255,0.08);
    border: 2px solid var(--sg-primary, #FFC43D);
    border-radius: 14px;
    color: var(--sg-primary, #FFC43D);
    font-family: 'Oswald', sans-serif;
    font-size: 36px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    animation: wai-reveal-points-pop 520ms cubic-bezier(0.18, 0.85, 0.4, 1.0) 360ms both;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('wai-reveal-style')) return;
  const s = document.createElement('style');
  s.id = 'wai-reveal-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export function createReveal(stage: HTMLElement): RevealHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'wai-reveal';
  el.setAttribute('data-scene', 'reveal');
  el.setAttribute('data-testid', 'wai-reveal');

  const eyebrow = document.createElement('div');
  eyebrow.className = 'wai-reveal-eyebrow';
  eyebrow.setAttribute('data-testid', 'wai-reveal-eyebrow');

  const name = document.createElement('div');
  name.className = 'wai-reveal-name';
  name.setAttribute('data-testid', 'wai-reveal-name');

  const pointsWrap = document.createElement('div');

  el.appendChild(eyebrow);
  el.appendChild(name);
  el.appendChild(pointsWrap);
  stage.appendChild(el);

  return {
    el,
    setReveal(r) {
      eyebrow.textContent = r.category ? r.category.toUpperCase() : 'WHO AM I?';
      name.textContent = r.subjectName;
      const restart = (e: HTMLElement): void => {
        e.style.animation = 'none';
        void e.offsetWidth;
        e.style.animation = '';
      };
      restart(name);

      pointsWrap.innerHTML = '';
      const correctsCount = r.corrects?.length ?? 0;
      if (correctsCount > 0) {
        const totalPts = r.corrects.reduce((s, c) => s + c.points, 0);
        const pts = document.createElement('div');
        pts.className = 'wai-reveal-points';
        pts.setAttribute('data-testid', 'wai-reveal-points');
        const playerWord = correctsCount === 1 ? 'player' : 'players';
        pts.innerHTML = `+${totalPts} <span class="small">pts · ${correctsCount} ${playerWord} got it</span>`;
        pointsWrap.appendChild(pts);
      } else {
        const nw = document.createElement('div');
        nw.className = 'wai-reveal-noWinner';
        nw.setAttribute('data-testid', 'wai-reveal-no-winner');
        nw.textContent = 'Nobody got it';
        pointsWrap.appendChild(nw);
      }
    },
    setVisible(v) {
      el.style.display = v ? 'flex' : 'none';
    },
  };
}
